import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactSVG } from 'react-svg';
import clsx from 'clsx';
import { CityDetailResults } from '@/types/marketplace';

interface CityListItemProps {
  city: CityDetailResults;
  imgSize?: number;
  imgClassName?: string;
  nameClassName?: string;
  textContainerClassName?: string;
  availableRoomsClassName?: string;
  availableSoonClassName?: string;
  noAvailableRoomsClassName?: string;
}

const CityListItem: React.FC<CityListItemProps> = ({
  city,
  imgSize = 28,
  imgClassName,
  nameClassName = 'text-[14px] font-bold leading-[16px]',
  textContainerClassName = 'relative ml-2 min-w-0 flex-1 text-left',
  availableRoomsClassName = 'text-[#1B998B]',
  availableSoonClassName = 'text-N700',
  noAvailableRoomsClassName = 'text-N700',
}) => {
  const { t, i18n } = useTranslation();
  const isComingSoon = city.regular_houses === 0;

  const cityName = useMemo(() => {
    const locale = i18n.language;
    return city.name_translations[locale] || city.name_translations.en;
  }, [city, i18n.language]);

  const placeholderImage = `https://placehold.co/${imgSize}x${imgSize}/e9ecef/495057?text=${encodeURIComponent(cityName)}`;

  return (
    <>
      <div className={imgClassName} style={{ width: imgSize, height: imgSize }}>
        <img
          src={city.cover_image_url}
          alt={cityName}
          width={imgSize}
          height={imgSize}
          style={{ objectFit: 'cover' }}
          className="h-full w-full rounded-[10px]"
          onError={(e) => {
            e.currentTarget.src = placeholderImage;
          }}
        />
      </div>
      <div className={textContainerClassName}>
        <div className="flex w-full items-center justify-between">
          <span className={nameClassName}>
            {cityName}
          </span>
        </div>
        {city.available_regular_rooms_count > 0 ? (
          <span className={clsx('text-start text-[12px] font-medium', availableRoomsClassName)}>
            {t('js.components.all_cities_dropdown.available_rooms', { count: city.available_regular_rooms_count })}
          </span>
        ) : isComingSoon && city.available_rooms_count > 0 ? (
          <span className={clsx('text-start text-[12px] font-medium', availableSoonClassName)}>
            {t('js.components.all_cities_dropdown.available_soon', { count: city.available_rooms_count })}
          </span>
        ) : (
          <span className={clsx('text-start text-[12px] font-medium', noAvailableRoomsClassName)}>
            {t('js.components.all_cities_dropdown.no_available_rooms')}
          </span>
        )}

        {isComingSoon ? (
          <span className="font-sofia-pro absolute right-0 top-1/2 -translate-y-1/2 rounded-full bg-[linear-gradient(22.08deg,#F71818_-22.56%,#F9441B_-9.98%,#FB7B1E_14.62%,#FEC600_58.21%,#FECA06_66.97%,#FED50E_75.17%,#FEE916_83.15%,#FFFF25_89.78%)] px-2 py-1 text-[12px] font-medium text-N900">
            {t('js.features.marketplace.components.room_search.coming_soon')}
          </span>
        ) : city.available_immediately_count > 0 ? (
          <span className="absolute right-0 top-1/2 -translate-y-1/2">
            <ReactSVG
              src={require(`@assets/images/marketplace/lighting_active.svg`)}
              className="searchbar-item-available-immediately"
              aria-hidden="true"
              beforeInjection={(svg) => {
                svg.setAttribute('width', `18`);
                svg.setAttribute('height', `18`);
                svg.setAttribute('aria-hidden', 'true');
              }}
            />
          </span>          
        ) : null}
      </div>
    </>
  );
};

export { CityListItem };
